export interface Rate {
  code: string;
  name: string;
  flag: string;
  buy: string;
  sell: string;
  change: string;
  up: boolean;
  category: "doviz" | "altin" | "metal";
}

interface V4Entry {
  Buying: number;
  Selling: number;
  Change: number;
  Type: string;
  Name?: string;
}

const CURRENCY_MAP: Record<string, { code: string; name: string; flag: string; category: Rate["category"] }> = {
  // Dövizler
  USD:  { code: "USD", name: "Amerikan Doları",        flag: "🇺🇸", category: "doviz" },
  EUR:  { code: "EUR", name: "Euro",                   flag: "🇪🇺", category: "doviz" },
  GBP:  { code: "GBP", name: "İngiliz Sterlini",      flag: "🇬🇧", category: "doviz" },
  CHF:  { code: "CHF", name: "İsviçre Frangı",        flag: "🇨🇭", category: "doviz" },
  JPY:  { code: "JPY", name: "Japon Yeni",             flag: "🇯🇵", category: "doviz" },
  CAD:  { code: "CAD", name: "Kanada Doları",          flag: "🇨🇦", category: "doviz" },
  AUD:  { code: "AUD", name: "Avustralya Doları",      flag: "🇦🇺", category: "doviz" },
  SAR:  { code: "SAR", name: "Suudi Arabistan Riyali", flag: "🇸🇦", category: "doviz" },
  AED:  { code: "AED", name: "BAE Dirhemi",            flag: "🇦🇪", category: "doviz" },
  RUB:  { code: "RUB", name: "Rus Rublesi",            flag: "🇷🇺", category: "doviz" },
  CNY:  { code: "CNY", name: "Çin Yuanı",              flag: "🇨🇳", category: "doviz" },
  NOK:  { code: "NOK", name: "Norveç Kronu",           flag: "🇳🇴", category: "doviz" },
  SEK:  { code: "SEK", name: "İsveç Kronu",            flag: "🇸🇪", category: "doviz" },
  DKK:  { code: "DKK", name: "Danimarka Kronu",        flag: "🇩🇰", category: "doviz" },
  KWD:  { code: "KWD", name: "Kuveyt Dinarı",          flag: "🇰🇼", category: "doviz" },
  QAR:  { code: "QAR", name: "Katar Riyali",           flag: "🇶🇦", category: "doviz" },
  // Altın
  GRA:              { code: "GAU", name: "Gram Altın",            flag: "🥇", category: "altin" },
  CEYREKALTIN:      { code: "CEY", name: "Çeyrek Altın",         flag: "🥇", category: "altin" },
  YARIMALTIN:       { code: "YAR", name: "Yarım Altın",          flag: "🥇", category: "altin" },
  TAMALTIN:         { code: "TAM", name: "Tam Altın",            flag: "🥇", category: "altin" },
  CUMHURIYETALTINI: { code: "CUM", name: "Cumhuriyet Altını",    flag: "🥇", category: "altin" },
  ATAALTIN:         { code: "ATA", name: "Ata Altın",            flag: "🥇", category: "altin" },
  "14AYARALTIN":    { code: "14A", name: "14 Ayar Altın",        flag: "🥇", category: "altin" },
  "18AYARALTIN":    { code: "18A", name: "18 Ayar Altın",        flag: "🥇", category: "altin" },
  HAS:              { code: "HAS", name: "Has Altın",            flag: "🥇", category: "altin" },
  RESATALTIN:       { code: "RST", name: "Reşat Altın",          flag: "🥇", category: "altin" },
  HAMITALTIN:       { code: "HMT", name: "Hamit Altın",          flag: "🥇", category: "altin" },
  // Kıymetli Madenler
  GUMUS: { code: "XAG", name: "Gram Gümüş",   flag: "🥈", category: "metal" },
  ONS:   { code: "ONS", name: "Ons Altın",     flag: "🥇", category: "metal" },
  GPL:   { code: "GPL", name: "Gram Platin",   flag: "⬜", category: "metal" },
  PAL:   { code: "PAL", name: "Gram Paladyum", flag: "⬜", category: "metal" },
};

function formatRate(value: number): string {
  if (value === 0) return "0.00";
  if (value >= 1000) return value.toFixed(2);
  if (value >= 100) return value.toFixed(2);
  if (value >= 1) return value.toFixed(4);
  return value.toFixed(4);
}

export async function fetchRates(): Promise<Rate[]> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 10000);

  try {
    const res = await fetch("https://finans.truncgil.com/v4/today.json", {
      signal: controller.signal,
    });

    if (!res.ok) throw new Error(`HTTP ${res.status}`);

    const data: Record<string, V4Entry | string> = await res.json();

    const rates: Rate[] = [];

    for (const [apiKey, meta] of Object.entries(CURRENCY_MAP)) {
      const entry = data[apiKey] as V4Entry | undefined;
      if (!entry || typeof entry === "string") continue;

      const buyNum = entry.Buying ?? 0;
      const sellNum = entry.Selling ?? 0;
      const changeNum = entry.Change ?? 0;

      if (buyNum === 0 && sellNum === 0) continue;

      rates.push({
        code: meta.code,
        name: meta.name,
        flag: meta.flag,
        buy: formatRate(buyNum),
        sell: formatRate(sellNum),
        change: Math.abs(changeNum).toFixed(2),
        up: changeNum >= 0,
        category: meta.category,
      });
    }

    return rates;
  } finally {
    clearTimeout(timeoutId);
  }
}

export const FALLBACK_RATES: Rate[] = [
  { code: "USD", name: "Amerikan Doları", flag: "🇺🇸", buy: "44.33", sell: "44.34", change: "0.07", up: true, category: "doviz" },
  { code: "EUR", name: "Euro", flag: "🇪🇺", buy: "51.19", sell: "51.21", change: "0.20", up: false, category: "doviz" },
  { code: "GBP", name: "İngiliz Sterlini", flag: "🇬🇧", buy: "58.98", sell: "59.01", change: "0.21", up: false, category: "doviz" },
  { code: "GAU", name: "Gram Altın", flag: "🥇", buy: "5987.00", sell: "5989.00", change: "6.52", up: false, category: "altin" },
  { code: "CEY", name: "Çeyrek Altın", flag: "🥇", buy: "9800.00", sell: "9850.00", change: "0.31", up: true, category: "altin" },
  { code: "XAG", name: "Gram Gümüş", flag: "🥈", buy: "39.45", sell: "39.82", change: "0.22", up: true, category: "metal" },
];
